import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import AppActions from 'app/shared/flux/actions/AppActions';
import {
  emitTrackBuildingPayPerLinkClickEvent,
  emitTrackUpperPpcClickEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanel } from '../../../ui/DetailPanel';
import { MiniMap } from '../../../ui/MiniMap';
import { NeighborhoodSpoke, NeighborhoodSpokeContent } from '../NeighborhoodModule/NeighborhoodSpoke';
import { NeighborhoodProvider } from '../NeighborhoodModule/context';
import { CostAndFeesSpokeContent } from '../CostAndFeesModule/CostAndFeesSpokeContent';
import { CostAndFeesSpoke } from '../CostAndFeesModule/CostAndFeesFooter';
import { BuildingWebsiteLink } from '../../../ui/BuildingWebsiteLink';
import { CaretRightIcon } from '../../../ui/icons';
import { DesktopCTAs } from './DesktopCTAs';
import { getListingSummaryDisplay } from './listingSummaryModule.utils';
import {
  addressText,
  addressTriggerRow,
  buildingName,
  container,
  costsFeesLink,
  costsFeesRow,
  costsFeesSeparator,
  detailsRow,
  priceRow,
  priceSuffix,
  priceText,
  websiteContainer,
} from './listingSummaryModule.styles';

const AddressTriggerContent = ({
  addressDisplay,
  lat,
  lon,
}: {
  addressDisplay: string;
  lat?: number;
  lon?: number;
}) => (
  <>
    {lat != null && lon != null && <MiniMap lat={lat} lon={lon} />}
    <span className={addressText}>{addressDisplay}</span>
  </>
);

export const ListingSummaryModule = () => {
  const dispatch = useDispatch();
  const { openPanel, isMobile } = useDetailPanel();
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  if (!currentListing) {
    return null;
  }

  const {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    sqftDisplay,
    showSqft,
    unitCount,
    showUnits,
    roomSqFt,
    showRoomSqFt,
    roommatesCount,
    showHousemates,
    housematesLabel,
    addressDisplay,
    websiteUrl,
    websiteLinkText,
  } = getListingSummaryDisplay(currentListing);

  const websiteAriaLabel = currentListing.displayName
    ? `Visit ${currentListing.displayName} website`
    : `Visit property website: ${websiteUrl}`;

  const geo = currentListing.geo;
  const address = currentListing.address;
  const showTotalPriceBadge = shouldShowTotalMonthlyPriceBadge(currentListing);

  const handleWebsiteClick = () => {
    dispatch(AppActions.sendEventToApi('building_website_visited', null));
    if (currentListing.maloneLotIdEncoded) {
      dispatch(AppActions.sendEventToApi('building_website_clicked', currentListing.maloneLotIdEncoded));
    }
    emitTrackUpperPpcClickEvent({ triggerLocation: 'home_details_halo' });
    emitTrackBuildingPayPerLinkClickEvent({
      triggerObject: 'home_details_component|summary',
      triggerLocation: 'home_details_halo',
    });
  };

  const handleOpenNeighborhood = () => {
    openPanel('neighborhood', NeighborhoodSpokeContent, { provider: NeighborhoodProvider });
  };

  const handleOpenCostsAndFees = () => {
    openPanel('cost-and-fees', CostAndFeesSpokeContent);
  };

  const renderAddressTrigger = () => {
    if (!address) {
      return null;
    }

    const triggerContent = <AddressTriggerContent addressDisplay={addressDisplay} lat={geo?.lat} lon={geo?.lon} />;

    if (!isMobile) {
      return (
        <button
          type="button"
          className={addressTriggerRow}
          onClick={handleOpenNeighborhood}
          aria-label={`View neighborhood for ${addressDisplay}`}
        >
          {triggerContent}
        </button>
      );
    }

    return (
      <FullscreenOverlay.Root defaultOpen={false}>
        <FullscreenOverlay.Trigger asChild>
          <button type="button" className={addressTriggerRow} aria-label={`View neighborhood for ${addressDisplay}`}>
            {triggerContent}
          </button>
        </FullscreenOverlay.Trigger>

        <FullscreenOverlay.Portal>
          <FullscreenOverlay.Backdrop>
            <FullscreenOverlay.Content>
              <NeighborhoodSpoke />
            </FullscreenOverlay.Content>
          </FullscreenOverlay.Backdrop>
        </FullscreenOverlay.Portal>
      </FullscreenOverlay.Root>
    );
  };

  const renderCostsFeesRow = () => {
    if (!showTotalPriceBadge) {
      return null;
    }

    const costsFeesButton = isMobile ? (
      <FullscreenOverlay.Root defaultOpen={false}>
        <FullscreenOverlay.Trigger asChild>
          <button type="button" className={costsFeesLink} aria-label="View costs and fees">
            Costs & fees <CaretRightIcon />
          </button>
        </FullscreenOverlay.Trigger>
        <FullscreenOverlay.Portal>
          <FullscreenOverlay.Backdrop>
            <FullscreenOverlay.Content>
              <CostAndFeesSpoke />
            </FullscreenOverlay.Content>
          </FullscreenOverlay.Backdrop>
        </FullscreenOverlay.Portal>
      </FullscreenOverlay.Root>
    ) : (
      <button type="button" className={costsFeesLink} onClick={handleOpenCostsAndFees} aria-label="View costs and fees">
        Costs & fees <CaretRightIcon />
      </button>
    );

    return (
      <div className={costsFeesRow}>
        <TotalMonthlyPriceBadge listing={currentListing} iconVariant="default" />
        <span className={costsFeesSeparator} aria-hidden="true">
          |
        </span>
        {costsFeesButton}
      </div>
    );
  };

  return (
    <div className={container}>
      {!shouldHideDetails && (
        <>
          {showPrice && (
            <div className={priceRow}>
              <span className={priceText}>{priceDisplay}</span>
              <span className={priceSuffix}>{'/ mo'}</span>
            </div>
          )}

          {renderCostsFeesRow()}

          {(showBedRange || showBathRange || showSqft || showUnits || showRoomSqFt || showHousemates) && (
            <div className={detailsRow}>
              {showBedRange && <span>{bedRangeDisplay}</span>}
              {showBedRange && showBathRange && <span> | </span>}
              {showBathRange && <span>{bathRangeDisplay}</span>}
              {(showBedRange || showBathRange) && showSqft && <span> | </span>}
              {showSqft && <span>{sqftDisplay}</span>}
              {(showBedRange || showBathRange || showSqft) && showUnits && <span> | </span>}
              {showUnits && <span>{unitCount} units available</span>}
              {(showBedRange || showBathRange || showSqft || showUnits) && showRoomSqFt && <span> | </span>}
              {showRoomSqFt && <span>{roomSqFt} sqft room</span>}
              {(showBedRange || showBathRange || showSqft || showUnits || showRoomSqFt) && showHousemates && (
                <span> | </span>
              )}
              {showHousemates && (
                <span>
                  {roommatesCount} {housematesLabel}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {currentListing.displayName && <div className={buildingName}>{currentListing.displayName}</div>}

      {websiteUrl && (
        <div className={websiteContainer}>
          <BuildingWebsiteLink
            websiteUrl={websiteUrl}
            linkText={websiteLinkText}
            ariaLabel={websiteAriaLabel}
            onClick={handleWebsiteClick}
          />
        </div>
      )}

      {renderAddressTrigger()}

      <DesktopCTAs />
    </div>
  );
};
